// ApiTester.tsx
import React, { useState } from 'react';
import { useWebSocket } from '../../contexts/backend/WebSocketContext';
import api from '../../services/api';
import { Loader2, Send, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { APP_VERSION } from '../../config/environment';

// ---- JsonEditor (lokal) ----
const JsonEditor: React.FC<{ value: string; onChange: (value: string) => void; }> = ({ value, onChange }) => {
    const [error, setError] = useState('');
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        onChange(newValue);
        try { JSON.parse(newValue); setError(''); }
        catch { setError('Invalid JSON'); }
    };
    return (
        <div>
      <textarea
          value={value}
          onChange={handleChange}
          rows={8}
          className={`w-full p-2 font-mono text-sm bg-gray-50 border rounded-md focus:ring-2 ${
              error ? 'border-red-500 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-400'
          }`}
          placeholder='{ "key": "value" }'
      />
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
    );
};
// ----------------------------

export const ApiTester: React.FC = () => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [apiType, setApiType] = useState<'rest' | 'websocket'>('rest');
    // Tipp: Für WebSocket direkt eine Action vorbelegen
    const [endpoint, setEndpoint] = useState('/auth/status'); // bei WS = action (z.B. "users.get")
    const [method, setMethod] = useState<'get' | 'post' | 'put' | 'delete'>('get');
    const [payload, setPayload] = useState('{}');
    const [response, setResponse] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    const { request, status } = useWebSocket();

    const handleCopyToClipboard = async () => {
        if (!response) return;
        try {
            await navigator.clipboard.writeText(JSON.stringify(response, null, 2));
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
        }
    };

    const handleSend = async () => {
        setIsLoading(true);
        setResponse(null);

        let parsedPayload: any;
        try {
            parsedPayload = JSON.parse(payload);
        } catch {
            setIsLoading(false);
            setResponse({ error: 'Invalid JSON payload.' });
            return;
        }

        if (apiType === 'rest') {
            try {
                const res = await api({
                    method,
                    url: endpoint,
                    data: method !== 'get' ? parsedPayload : undefined,
                });
                setResponse({ status: res.status, data: res.data });
            } catch (err: any) {
                setResponse({
                    error: 'Request Failed',
                    status: err.response?.status,
                    data: err.response?.data,
                });
            } finally {
                setIsLoading(false);
            }
        } else {
            try {
                // endpoint ist hier die WS-Action, z.B. "users.get" / "achievements.list"
                const res = await request<any>(endpoint, parsedPayload, 20000);
                setResponse(res);
            } catch (e: any) {
                setResponse({ error: e?.message || 'Request failed' });
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="fixed bottom-4 left-4 w-96 bg-white border border-gray-300 rounded-lg shadow-2xl z-[100000] max-h-[800px] flex flex-col">
            <div
                className="flex items-center justify-between p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors flex-shrink-0"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-800">API Tester</h3>
                    <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">v{APP_VERSION}</span>
                </div>
                <button className="text-gray-600 hover:text-gray-800 transition-colors">
                    {isCollapsed ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
            </div>

            {!isCollapsed && (
                <div className="p-4 space-y-4 overflow-y-auto flex-1">
                    <div className="flex bg-gray-100 rounded-md p-1">
                        <button onClick={() => setApiType('rest')} className={`flex-1 p-1 text-sm rounded ${apiType === 'rest' ? 'bg-white shadow' : ''}`}>REST</button>
                        <button onClick={() => setApiType('websocket')} className={`flex-1 p-1 text-sm rounded ${apiType === 'websocket' ? 'bg-white shadow' : ''}`}>WebSocket</button>
                    </div>

                    {apiType === 'rest' ? (
                        <div className="flex items-center space-x-2">
                            <select value={method} onChange={(e) => setMethod(e.target.value as any)} className="p-2 border rounded-md">
                                <option value="get">GET</option>
                                <option value="post">POST</option>
                                <option value="put">PUT</option>
                                <option value="delete">DELETE</option>
                            </select>
                            <input type="text" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="/your/endpoint" className="flex-1 p-2 border rounded-md" />
                        </div>
                    ) : (
                        <div>
                            <label className="text-sm font-medium text-gray-600">Action</label>
                            <input
                                type="text"
                                value={endpoint}
                                onChange={(e) => setEndpoint(e.target.value)}
                                placeholder="users.get"
                                className="w-full p-2 border rounded-md mt-1"
                            />
                            {status !== 'open' && <p className="text-xs text-amber-600 mt-1">WebSocket Status: {status}</p>}
                        </div>
                    )}

                    <div>
                        <label className="text-sm font-medium text-gray-600">Payload</label>
                        <JsonEditor value={payload} onChange={setPayload} />
                    </div>

                    <button onClick={handleSend} disabled={isLoading} className="w-full bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 disabled:bg-blue-300 flex items-center justify-center">
                        {isLoading ? <Loader2 className="animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Send Request
                    </button>

                    {response && (
                        <div>
                            <div className="flex items-center justify-between border-t pt-2 mt-2">
                                <h4 className="text-sm font-semibold text-gray-800">Response</h4>
                                <button
                                    onClick={handleCopyToClipboard}
                                    className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                                    title="Copy to clipboard"
                                >
                                    {isCopied ? (
                                        <>
                                            <Check className="w-3 h-3" />
                                            <span>Copied!</span>
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-3 h-3" />
                                            <span>Copy</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            <pre className="w-full bg-gray-800 text-white text-xs p-2 rounded-md mt-2 overflow-x-auto max-h-64 overflow-y-auto">
                {JSON.stringify(response, null, 2)}
              </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};